<template>
    <TransitionRoot
        as="template"
        :show="state.open"
    >
        <Dialog
            as="div"
            class="relative"
            :open="state.open === true"
            @close="dialogClose(state.overlayPreventClose)"
        >
            <TransitionChild
                as="template"
                enter="ease-out duration-300"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="ease-in duration-200"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div class="fixed inset-0 z-[6800] bg-black opacity-75 transition-opacity" />
            </TransitionChild>

            <div class="fixed inset-0 z-[6802] overflow-y-auto">
                <div
                    class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0"
                >
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0 translate-y-4 sm:translate-y-0 scale-0"
                        enter-to="opacity-100 translate-y-0 scale-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100 translate-y-0 scale-100"
                        leave-to="opacity-0 translate-y-4 sm:translate-y-0 scale-0"
                    >
                        <DialogPanel
                            class="relative w-full max-w-3xl transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:p-6"
                        >
                            <div class="absolute top-0 right-0 pt-4 pr-4">
                                <button
                                    type="button"
                                    class="focus:ring-none cursor-pointer rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-hidden"
                                    @click="dialogClose()"
                                >
                                    <span class="sr-only">Close</span>
                                    <MdiClose
                                        class="h-6 w-6"
                                        aria-hidden="true"
                                    />
                                </button>
                            </div>
                            <div class="sm:flex sm:items-start">
                                <div
                                    class="mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gray-200 sm:mx-0 sm:h-10 sm:w-10"
                                >
                                    <MdiTooltipPlus
                                        v-if="templateId === '0'"
                                        class="h-6 w-6 text-gray-600"
                                        aria-hidden="true"
                                    />
                                    <MdiReceiptTextEdit
                                        v-if="templateId !== '0'"
                                        class="h-6 w-6 text-gray-600"
                                        aria-hidden="true"
                                    />
                                </div>
                                <div
                                    class="mt-3 text-center sm:mt-0 sm:ml-4 sm:flex-1 sm:text-left"
                                >
                                    <DialogTitle
                                        as="h3"
                                        class="pt-2 text-lg leading-6 font-medium text-gray-900 select-none"
                                    >
                                        {{ state.title }}
                                    </DialogTitle>
                                    <div class="www py-2 select-none">
                                        <label
                                            for="template-type"
                                            class="block text-sm font-medium text-gray-700"
                                        >
                                            {{ t('templateTypeChoose') }}
                                        </label>
                                        <select
                                            id="template-type"
                                            v-model="template.type"
                                            name="template-type"
                                            :disabled="templateId !== '0'"
                                            class="focus:border-blue-500cd mod mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-sm focus:ring-blue-500 focus:outline-hidden sm:text-sm"
                                        >
                                            <option
                                                v-for="templateType in config.templates.allowTypes"
                                                :key="templateType"
                                            >
                                                {{ templateType }}
                                            </option>
                                        </select>
                                    </div>

                                    <div class="www py-2 select-none">
                                        <label
                                            for="template-name"
                                            class="block text-sm font-medium text-gray-700"
                                        >
                                            {{ t('templateName') }}
                                        </label>
                                        <input
                                            id="template-name"
                                            v-model="template.name"
                                            type="text"
                                            name="template-name"
                                            autocomplete="off"
                                            :class="[
                                                template.name.length === 0
                                                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                                                    : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500',
                                                'mt-1 block w-full rounded-md border px-3 py-2 text-xs shadow-sm focus:outline-hidden sm:text-sm',
                                            ]"
                                            @input="
                                                if (templateId === '0') {
                                                    codeize(template.name);
                                                }
                                            "
                                        />
                                    </div>
                                    <div class="www py-2 select-none">
                                        <label
                                            for="template-description"
                                            class="block text-sm font-medium text-gray-700"
                                        >
                                            {{ t('templateDescription') }}
                                        </label>
                                        <input
                                            id="template-description"
                                            v-model="template.description"
                                            type="text"
                                            name="template-description"
                                            autocomplete="off"
                                            class="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-xs shadow-sm focus:border-blue-500 focus:ring-blue-500 focus:outline-hidden sm:text-sm"
                                        />
                                    </div>
                                    <div class="www py-2 select-none">
                                        <label
                                            for="template-code"
                                            class="block text-sm font-medium text-gray-700"
                                        >
                                            {{ t('templateCode') }}
                                        </label>
                                        <div class="relative w-full">
                                            <input
                                                id="template-code"
                                                ref="codeInput"
                                                v-model="template.code"
                                                type="text"
                                                name="template-code"
                                                autocomplete="off"
                                                :readonly="saving.codeLock"
                                                :disabled="saving.codeLock"
                                                class="block w-full rounded-md border px-3 py-2 shadow-sm"
                                                :class="[
                                                    template.code.length === 0
                                                        ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                                                        : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500',
                                                    'mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 pr-10 text-xs shadow-sm focus:border-amber-500 focus:ring-amber-500 focus:outline-hidden sm:text-sm',
                                                ]"
                                            />
                                            <button
                                                type="button"
                                                :class="[
                                                    templateId === '0'
                                                        ? 'cursor-not-allowed text-gray-300'
                                                        : 'cursor-pointer text-gray-700 hover:bg-gray-50',
                                                    'focus:ring-none absolute top-0 right-0 m-px inline-flex items-center rounded-r-md border-0 border-l border-gray-300 bg-gray-100 px-3 py-2 focus:outline-hidden',
                                                ]"
                                                :disabled="templateId === '0'"
                                                @click="
                                                    saving.codeLock = !saving.codeLock;
                                                    if (!saving.codeLock) {
                                                        nextTick(() => {
                                                            codeInput.focus();
                                                        });
                                                    } else {
                                                        nextTick(() => {
                                                            codeInput.blur();
                                                        });
                                                    }
                                                "
                                            >
                                                <MdiLock
                                                    v-if="saving.codeLock === true"
                                                    class="h-4 w-4 sm:h-5 sm:w-5"
                                                />
                                                <MdiLockOpenVariant
                                                    v-if="saving.codeLock === false"
                                                    class="h-4 w-4 sm:h-5 sm:w-5"
                                                />
                                            </button>
                                        </div>
                                        <AlertBox
                                            v-if="!saving.codeLock && templateId !== '0'"
                                            state="warning"
                                            :icon="MdiAlert"
                                            :title="t('templateCodeChangeWarning')"
                                            class-override="rounded-t-none"
                                        ></AlertBox>
                                    </div>
                                    <ImportTemplateVersion
                                        v-if="templateId === '0'"
                                        :template="template"
                                        :template-version-clone="templateVersionClone"
                                    />
                                    <AlertBox
                                        v-if="
                                            templateVersionClone.active === true &&
                                            Object.keys(templateVersionClone.data).length === 0
                                        "
                                        state="warning"
                                        :icon="MdiAlert"
                                        :title="t('templateSelectForClone')"
                                        class-override="mt-1"
                                    ></AlertBox>
                                    <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                                        <ActionButton
                                            :value="t('buttonSave')"
                                            state="primary"
                                            :icon="MdiContentSave"
                                            :is-disabled="allowSaveAction()"
                                            @click="save('0', template, saving)"
                                        />
                                    </div>
                                </div>
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>

    <Spinner
        v-if="saving.active"
        type="complex"
        :color="`text-blue-600`"
        :color2="`text-blue-400`"
        :text-color="`text-gray-200`"
        :overlay="true"
        :speed="1000"
        :dots="true"
        :dot-char="'.'"
        :dot-dual="false"
        :dot-reverse="false"
        :text="t('loading')"
        :inline="false"
        :pulse="true"
    />
</template>

<script setup>
import { reactive, ref, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { t } from '../composables/i18n';
import { stringToCode, toLatin128, keyToStr, guessValidDbObject } from '../composables/utils';
import { getTemplates, saveTemplate, saveTemplateVersion } from '../composables/useTemplates';
import Spinner from '../ui/Spinner.vue';
import ImportTemplateVersion from '../components/ImportTemplateVersion.vue';
import AlertBox from '../ui/AlertBox.vue';
import ActionButton from '../ui/ActionButton.vue';
import { notify } from 'notiwind';
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';
import {
    MdiTooltipPlus,
    MdiClose,
    MdiContentSave,
    MdiReceiptTextEdit,
    MdiLock,
    MdiLockOpenVariant,
    MdiAlert,
} from 'materialdesignicons-vue3/icons/';

defineExpose({
    name: 'TemplateEditModal',
});

const props = defineProps({
    state: {
        type: Object,
        required: true,
    },
    templateId: {
        type: String,
        required: true,
    },
    template: {
        type: Object,
        required: true,
    },
});

const router = useRouter();
const template = props.template;
const templateId = props.templateId;
const state = props.state;
const templateVersionClone = reactive({
    data: {},
    active: false,
});

const codeInput = ref(null);

keyToStr(template, ['name', 'code', 'description']);

const saving = reactive({
    active: false,
    codeLock: templateId !== '0',
    text: '',
});

const codeize = () => {
    template.code = toLatin128(stringToCode(template.name).toUpperCase());
};

const removeCurrentTemplate = (templateId, checkByCode) => {
    return checkByCode?.items.filter((item) => {
        return item.response.templateId !== templateId;
    });
};

const templateAttributeExists = async (templateId, template) => {
    const checkByCode = await getTemplates({
        code: template.code,
    });
    if (
        templateId === '0' &&
        Array.isArray(checkByCode?.items || false) &&
        checkByCode.items.length > 0
    ) {
        //console.log('checkByCode', templateId, checkByCode);
        return true;
    }
    if (templateId !== '0') {
        const checkByCodeUpdate =
            Array.isArray(checkByCode?.items || false) && checkByCode.items.length > 0
                ? removeCurrentTemplate(templateId, checkByCode)
                : [];
        return checkByCodeUpdate.length > 0;
    }
    return false;
};

const save = async () => {
    const templateExists = await templateAttributeExists(templateId, template);

    if (templateExists) {
        notify(
            {
                group: 'error',
                title: t('notifyError'),
                text: t('templateExists'),
            },
            7000,
        );
    } else {
        const templateResult = await saveTemplate(templateId, template, saving);

        if (guessValidDbObject(templateResult) === true) {
            notify(
                {
                    group: 'success',
                    title: t('notifyOk'),
                    text: `${t('notifyMessageSaved')}`,
                },
                2000,
            );

            if (
                templateVersionClone.active === true &&
                Object.keys(templateVersionClone.data).length > 0
            ) {
                templateVersionClone.data.version = '1.0';
                templateVersionClone.data.templateVersionId = '0';
                templateVersionClone.data.templateId = templateResult.templateId;
                const templateVersionResult = await saveTemplateVersion(
                    '0',
                    templateVersionClone.data,
                );

                if (guessValidDbObject(templateVersionResult) === true) {
                    notify(
                        {
                            group: 'success',
                            title: t('notifyOk'),
                            text: `${t('notifyMessageSaved')}`,
                        },
                        2000,
                    );
                    dialogClose();
                    router.push({
                        name: 'template-version-edit',
                        params: {
                            templateid: templateVersionResult.templateId,
                            templateversionid: templateVersionResult.templateVersionId,
                        },
                    });
                }
            } else {
                dialogClose();
                router.push({
                    name: 'template-versions',
                    params: {
                        templateid: templateResult.templateId,
                    },
                });
            }
        }
    }
    return false;
};

const allowSaveAction = () => {
    return (
        state.open === false ||
        template.name.length === 0 ||
        template.code.length === 0 ||
        (templateVersionClone.active === true &&
            Object.keys(templateVersionClone.data).length === 0)
    );
};

const dialogClose = (preventClose = false) => {
    if (preventClose === false) {
        state.open = false;
    }
};
</script>
